import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ProductCard from "../ProductCard";
import { styled } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";

const NavButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 20,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  width: 44,
  height: 44,
  borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
}));

export default function RelatedProduct({ productId }) {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    async function fetchRelated() {
      try {
        const res = await fetch(`/productos/relacionados/${productId}/`);
        const data = await res.json();

        const formatted = (Array.isArray(data) ? data : []).map((p) => {
          const hasPromo = p.tipo_promocion || p.precio_descuento !== null || p.has_promotion;
          let discountPercent = null;

          if (p.tipo_promocion === "descuento" && typeof p.valor_promocion === "number") {
            discountPercent = Math.round(p.valor_promocion);
          } else if (p.tipo_promocion === "precio_fijo" && typeof p.precio_descuento === "number" && Number(p.pvp1) > 0) {
            const pct = (1 - p.precio_descuento / p.pvp1) * 100;
            discountPercent = Math.max(0, Math.round(pct));
          }

          return {
            id: p.id,
            code: p.code,
            name: p.name,
            slug: p.slug,
            section: p.section,
            category: p.category,
            brand: p.brand,
            pvp1: p.pvp1,
            images: p.images || [],
            variants: p.variants || [],
            tipo_promocion: p.tipo_promocion || null,
            valor_promocion: p.valor_promocion ?? null,
            precio_descuento: p.precio_descuento ?? null,
            hasPromotion: !!hasPromo,
            discountPercent,
          };
        });

        setRelated(formatted);
      } catch {
        setRelated([]);
      } finally {
        setLoading(false);
      }
    }
    fetchRelated();
  }, [productId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!related.length) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", mt: 6, px: { xs: 1.5, sm: 2 }, position: "relative" }}>
        <Typography
          variant="h6"
          sx={{
            mb: { xs: 2, sm: 3 },
            color: "#003366",
            fontWeight: "bold",
            textAlign: "center",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          Productos Relacionados
        </Typography>

        {!isMobile && (
          <>
            <NavButton ref={prevRef} sx={{ left: -18 }}>
              <ChevronLeftIcon fontSize="medium" />
            </NavButton>
            <NavButton ref={nextRef} sx={{ right: -18 }}>
              <ChevronRightIcon fontSize="medium" />
            </NavButton>
          </>
        )}

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          onInit={(swiper) => {
            if (!isMobile) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }
          }}
          loop
          allowTouchMove={isMobile}
          preventClicks={false}
          preventClicksPropagation={false}
          touchStartPreventDefault={false}
          spaceBetween={12}
          slidesPerView={1}
          pagination={{
            el: ".related-swiper-pagination",
            clickable: true,
            bulletClass: "swiper-pagination-bullet related-bullet",
            bulletActiveClass: "swiper-pagination-bullet-active related-bullet-active",
          }}
          autoplay={isMobile ? { delay: 3000, disableOnInteraction: false } : false}
          breakpoints={{
            600: { slidesPerView: 2, spaceBetween: 14 },
            900: { slidesPerView: 3, spaceBetween: 16 },
            1200: { slidesPerView: 4, spaceBetween: 16 },
          }}
        >
          {related.map((product) => (
            <SwiperSlide key={product.id}>
              <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
                <ProductCard product={product} />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>

        <Box
          className="related-swiper-pagination"
          sx={{
            display: { xs: "flex", sm: "none" },
            justifyContent: "center",
            gap: 1,
            mt: 1.5,
            "& .related-bullet": {
              width: 8,
              height: 8,
              background: "#cfd8dc",
              opacity: 1,
              borderRadius: "50%",
            },
            "& .related-bullet-active": {
              background: "#1565C0",
            },
          }}
        />
      </Box>
    </motion.div>
  );
}
